import type React from 'react'
import { useRef } from 'react'
import { Textarea } from '@neo/primitives/textarea'
import { Label } from '@neo/primitives/label'
import { Variable, Bold, Italic, Strikethrough, Smile, Paperclip } from 'lucide-react'
import { Tooltip } from '@neo/primitives/tooltip'
import { cn } from '@neo/lib/utils'
import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react'
import { useDropzone } from 'react-dropzone'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'

interface BodyInputProps {
  value: string
  onChange: (value: string) => void
  label: string
  formattingOptions?: FormattingOptions[]
  className?: string
  topSection?: React.ReactNode
  counter?: boolean
  onFileSelected?: (files: FileList) => void
  acceptedFileTypes?: string
  multiple?: boolean
  SubmitComponent?: React.ReactNode
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void
}

export enum FormattingOptions {
  BOLD = 'bold',
  ITALIC = 'italic',
  STRIKETHROUGH = 'strikethrough',
  EMOJI = 'emoji',
  VARIABLE = 'variable',
  FILE_UPLOAD = 'file_upload',
}

export const ChatInput = ({
  value,
  onChange,
  label,
  className,
  topSection,
  counter = true,
  formattingOptions = [
    FormattingOptions.BOLD,
    FormattingOptions.ITALIC,
    FormattingOptions.STRIKETHROUGH,
    FormattingOptions.EMOJI,
    FormattingOptions.VARIABLE,
  ],
  onFileSelected,
  multiple = false,
  SubmitComponent = null,
  onKeyDown,
  ...props
}: BodyInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const wrapSelectedText = (wrap: string) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)

    if (selectedText.includes('{{')) {
      const newText = `${value.substring(0, start)}${wrap}${selectedText}${wrap}${value.substring(end)}`
      onChange(newText)
    } else {
      const newText = `${value.substring(0, start)}${wrap}${selectedText}${wrap}${value.substring(end)}`
      onChange(newText)
    }
    textarea.focus()
  }

  const handleAddVariable = (ref: React.RefObject<HTMLTextAreaElement>) => {
    const textarea = ref.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    const existingVariables = value.match(/{{\d+}}/g) || []
    const currentCount = existingVariables.length

    const newVariable = `{{${currentCount + 1}}}`

    const updatedText = value.substring(0, start) + newVariable + value.substring(end)

    onChange(updatedText)
    textarea.focus()
    textarea.setSelectionRange(start + newVariable.length, start + newVariable.length)
  }

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newText = value.slice(0, start) + emojiData.emoji + value.slice(end)
    onChange(newText)
    textarea.focus()
    textarea.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length)
  }

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'audio/mpeg': ['.mp3'],
      'video/mp4': ['.mp4'],
    },
    onDrop: (acceptedFiles) => {
      if (onFileSelected) {
        onFileSelected(acceptedFiles as unknown as FileList)
      }
    },
  })

  return (
    <div className="relative flex flex-col gap-1.5" {...getRootProps()}>
      {topSection}
      {label && <Label className="mb-1.5 block">{label}</Label>}
      <div
        className={cn(
          'w-full rounded-md border border-st1 bg-background relative',
          'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
          'flex flex-col overflow-hidden resize-y',
          className,
        )}
      >
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => onKeyDown?.(e)}
          maxLength={1024}
          className={cn(
            'border-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            'min-h-[100px] max-h-[300px]',
            'resize-none shadow-none',
          )}
          {...props}
        />
        <div className="p-2 flex items-center justify-between mt-auto bg-background">
          <div className="flex gap-2">
            {formattingOptions?.includes(FormattingOptions.VARIABLE) && (
              <Tooltip content="Insert variable">
                <button
                  type="button"
                  className="cursor-pointer"
                  onClick={() => handleAddVariable(textareaRef)}
                  aria-label="Insert variable"
                >
                  <Variable className="size-4" />
                </button>
              </Tooltip>
            )}
            {formattingOptions?.includes(FormattingOptions.BOLD) && (
              <Tooltip content="Bold text">
                <button
                  type="button"
                  className="cursor-pointer"
                  onClick={() => wrapSelectedText('*')}
                  aria-label="Bold text"
                >
                  <Bold className="size-4" />
                </button>
              </Tooltip>
            )}
            {formattingOptions?.includes(FormattingOptions.ITALIC) && (
              <Tooltip content="Italic text">
                <button
                  type="button"
                  className="cursor-pointer"
                  onClick={() => wrapSelectedText('_')}
                  aria-label="Italic text"
                >
                  <Italic className="size-4" />
                </button>
              </Tooltip>
            )}
            {formattingOptions?.includes(FormattingOptions.STRIKETHROUGH) && (
              <Tooltip content="Strikethrough text">
                <button
                  type="button"
                  className="cursor-pointer"
                  onClick={() => wrapSelectedText('~')}
                  aria-label="Strikethrough text"
                >
                  <Strikethrough className="size-4" />
                </button>
              </Tooltip>
            )}
            {formattingOptions?.includes(FormattingOptions.EMOJI) && (
              <Popover>
                <Tooltip content="Insert emoji">
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="cursor-pointer"
                      aria-label="Insert emoji"
                      data-testid="Insert emoji"
                    >
                      <Smile className="size-4" />
                    </button>
                  </PopoverTrigger>
                </Tooltip>
                <PopoverContent
                  className="p-0 border-none shadow-lg"
                  side="top"
                  align="start"
                  sideOffset={10}
                >
                  <EmojiPicker onEmojiClick={(emojiData) => handleEmojiSelect(emojiData)} />
                </PopoverContent>
              </Popover>
            )}
            {formattingOptions?.includes(FormattingOptions.FILE_UPLOAD) && (
              <button
                type="button"
                className="cursor-pointer"
                onClick={openFileDialog}
                aria-label="Upload file"
                data-testid="Upload file"
              >
                <Paperclip className="size-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            {counter && <em className="text-xs text-muted-foreground">{value.length} / 1024</em>}
            {Boolean(SubmitComponent) && SubmitComponent}
          </div>
        </div>
      </div>
      <input {...getInputProps()} />
    </div>
  )
}
